import React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "@outpave/ds";
import { activateOnKey } from "utils/activateOnKey";
import { formatAddressLine, type AddressLineParts } from "utils/addressFormat";

export interface AddressFieldProps {
  label: React.ReactNode;
  /** The structured address; the field shows it as one line. */
  address: AddressLineParts | null | undefined;
  /** Opens the drawer that edits the address. */
  onOpen: () => void;
  /** Empties an optional address; a required one passes none and offers no clear. */
  onClear?: () => void;
  name?: string;
  placeholder?: string;
  required?: boolean;
  errorText?: React.ReactNode;
  helperText?: React.ReactNode;
  trailing?: React.ReactNode;
}

/**
 * A read-only field that opens the structured address drawer, by click, Enter or Space.
 * The caller owns the drawer.
 */
const AddressField: React.FC<AddressFieldProps> = ({
  label,
  address,
  onOpen,
  onClear,
  name,
  placeholder,
  required,
  errorText,
  helperText,
  trailing,
}) => {
  const { t } = useTranslation();

  return (
    <TextField
      fullWidth
      readOnly
      name={name}
      label={label}
      placeholder={placeholder}
      required={required}
      value={formatAddressLine(address)}
      onClick={onOpen}
      onKeyDown={activateOnKey(onOpen)}
      onClear={onClear}
      labels={{ clear: t("Clear address") }}
      // ARIA permits aria-haspopup on a textbox but not role="button" on a text input.
      inputProps={{ "aria-haspopup": "dialog" }}
      errorText={errorText}
      helperText={helperText}
      trailing={trailing}
    />
  );
};

export default AddressField;
